import { useState, useEffect } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Plus, Trash2, Save, X, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPE_LABELS = {
  invoice: "Invoices",
  quote: "Quotes",
  statement: "Statements",
  credit_note: "Credit Notes",
  receipt: "Receipts"
};

const ALL_DOCUMENT_TYPES = ["invoice", "quote", "statement", "credit_note", "receipt"];

export const AdditionalEmails = ({ customerId, customerName, primaryEmail }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newDocTypes, setNewDocTypes] = useState([...ALL_DOCUMENT_TYPES]);
  const [editingEmail, setEditingEmail] = useState(null);
  const [editDocTypes, setEditDocTypes] = useState([]);
  const [editLabel, setEditLabel] = useState("");

  useEffect(() => {
    if (customerId && expanded) {
      fetchEmails();
    }
  }, [customerId, expanded]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/customers/${customerId}/additional-emails`);
      setEmails(res.data.additional_emails || []);
    } catch (err) {
      console.error("Error fetching additional emails:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (newDocTypes.length === 0) {
      toast.error("Please select at least one document type");
      return;
    }

    try {
      await saveWithRetry(() => axios.post(`${API}/customers/${customerId}/additional-emails`, {
        email: newEmail.trim(),
        document_types: newDocTypes,
        label: newLabel.trim() || null
      }));
      toast.success("Email added successfully");
      setNewEmail("");
      setNewLabel("");
      setNewDocTypes([...ALL_DOCUMENT_TYPES]);
      setShowAddForm(false);
      fetchEmails();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to add email"));
    }
  };

  const handleUpdateEmail = async (email) => {
    if (editDocTypes.length === 0) {
      toast.error("Please select at least one document type");
      return;
    }

    try {
      await saveWithRetry(() => axios.put(`${API}/customers/${customerId}/additional-emails/${encodeURIComponent(email)}`, {
        document_types: editDocTypes,
        label: editLabel.trim() || null
      }));
      toast.success("Email updated successfully");
      setEditingEmail(null);
      fetchEmails();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to update email"));
    }
  };

  const handleDeleteEmail = async (email) => {
    if (!confirm(`Remove ${email} from this customer?`)) return;

    try {
      await saveWithRetry(() => axios.delete(`${API}/customers/${customerId}/additional-emails/${encodeURIComponent(email)}`));
      toast.success("Email removed");
      fetchEmails();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to remove email"));
    }
  };

  const startEditing = (emailObj) => {
    setEditingEmail(emailObj.email);
    setEditDocTypes([...emailObj.document_types]);
    setEditLabel(emailObj.label || "");
  };

  const toggleNewDocType = (type) => {
    setNewDocTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const toggleEditDocType = (type) => {
    setEditDocTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  if (!customerId) return null;

  return (
    <Card className="mt-4 border-blue-200">
      <CardHeader 
        className="pb-2 cursor-pointer hover:bg-blue-50 rounded-t-lg transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <CardTitle className="text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-blue-600" />
            Additional Email Recipients
            {emails.length > 0 && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                {emails.length}
              </span>
            )}
          </div>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </CardTitle>
      </CardHeader>
      
      {expanded && (
        <CardContent className="pt-2 space-y-3">
          {/* Primary email note */}
          {primaryEmail && (
            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
              <strong>Primary email:</strong> {primaryEmail} (receives all document types)
            </div>
          )}

          {/* Existing emails */}
          {loading ? (
            <div className="text-sm text-gray-400 text-center py-4">Loading...</div>
          ) : emails.length === 0 ? (
            <div className="text-sm text-gray-400 text-center py-4">
              No additional emails configured
            </div>
          ) : (
            <div className="space-y-2">
              {emails.map((emailObj, idx) => (
                <div key={idx} className="border rounded-lg p-3 bg-white">
                  {editingEmail === emailObj.email ? (
                    /* Edit mode */
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{emailObj.email}</span>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setEditingEmail(null)}>
                            <X className="h-3 w-3" />
                          </Button>
                          <Button size="sm" onClick={() => handleUpdateEmail(emailObj.email)}>
                            <Save className="h-3 w-3 mr-1" /> Save
                          </Button>
                        </div>
                      </div>
                      <Input
                        placeholder="Label (optional)"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        className="h-8 text-sm"
                      />
                      <div className="flex flex-wrap gap-2 mt-2">
                        {ALL_DOCUMENT_TYPES.map(type => (
                          <label key={type} className="flex items-center gap-1 text-xs cursor-pointer">
                            <Checkbox
                              checked={editDocTypes.includes(type)}
                              onCheckedChange={() => toggleEditDocType(type)}
                            />
                            {DOCUMENT_TYPE_LABELS[type]}
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : (
                    /* View mode */
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium text-sm flex items-center gap-2">
                          {emailObj.email}
                          {emailObj.label && (
                            <span className="text-xs text-gray-500">({emailObj.label})</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {emailObj.document_types.map(type => (
                            <span key={type} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">
                              {DOCUMENT_TYPE_LABELS[type]}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-7 w-7 p-0"
                          onClick={() => startEditing(emailObj)}
                        >
                          <span className="text-xs">Edit</span>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteEmail(emailObj.email)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add new email form */}
          {showAddForm ? (
            <div className="border-2 border-dashed border-blue-300 rounded-lg p-3 space-y-2 bg-blue-50">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Add New Email</Label>
                <Button size="sm" variant="ghost" onClick={() => setShowAddForm(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="email"
                  placeholder="Email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="h-9"
                  data-testid="add-email-input"
                />
                <Input
                  placeholder="Label (optional)"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600 mb-1 block">Document types to receive:</Label>
                <div className="flex flex-wrap gap-3">
                  {ALL_DOCUMENT_TYPES.map(type => (
                    <label key={type} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <Checkbox
                        checked={newDocTypes.includes(type)}
                        onCheckedChange={() => toggleNewDocType(type)}
                      />
                      {DOCUMENT_TYPE_LABELS[type]}
                    </label>
                  ))}
                </div>
              </div>
              <Button 
                className="w-full mt-2" 
                onClick={handleAddEmail}
                data-testid="confirm-add-email-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Email
              </Button>
            </div>
          ) : (
            <Button 
              variant="outline" 
              className="w-full border-dashed"
              onClick={() => setShowAddForm(true)}
              data-testid="add-additional-email-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add Additional Email
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  );
};
